import React, { useState } from "react";
import { useWebSocket } from "../hooks/useWebSocket";
import { useApi, API } from "../hooks/useApi";

const s = {
  tab:   (active) => ({
    padding: "5px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "11px",
    background: active ? "#1e40af" : "#1e2a45",
    color: active ? "#fff" : "#94a3b8", border: "none", fontWeight: 600,
  }),
  dot:   (ok) => ({
    width: "7px", height: "7px", borderRadius: "50%", flexShrink: 0,
    background: ok ? "#22c55e" : "#ef4444",
    boxShadow: ok ? "0 0 5px #22c55e" : "0 0 5px #ef4444",
  }),
  label: (ok) => ({ color: ok ? "#22c55e" : "#ef4444" }),
  restartBtn: (r) => ({
    padding: "4px 10px", borderRadius: "5px", cursor: r ? "not-allowed" : "pointer",
    fontSize: "11px", fontWeight: 600, border: "1px solid #7f1d1d",
    background: r ? "#1e2a45" : "#1a0a0a", color: r ? "#475569" : "#f87171",
    opacity: r ? 0.6 : 1,
  }),
  userBadge: (role) => ({
    padding: "3px 8px", borderRadius: "5px", fontSize: "11px", fontWeight: 600,
    background: role === "admin" ? "#1e40af" : role === "trader" ? "#065f46" : "#1e2a45",
    color:      role === "admin" ? "#93c5fd" : role === "trader" ? "#6ee7b7" : "#94a3b8",
  }),
  logoutBtn: {
    padding: "4px 10px", borderRadius: "5px", cursor: "pointer",
    fontSize: "11px", fontWeight: 600, border: "1px solid #1e2a45",
    background: "#0a0f1e", color: "#94a3b8",
  },
};

export default function Header({ tab, setTab, user, onLogout, brainOnline = true }) {
  const { connected: wsConnected } = useWebSocket("feed");
  const { data: engineData }       = useApi("/engine_status", 10000);
  const { data: statusData }       = useApi("/status", 30000);
  const [restarting, setRestarting] = useState(false);

  const mt5Ok  = engineData?.mt5_connected ?? false;
  const apiOk  = statusData?.api ?? false;
  const yfOk   = statusData?.yfinance ?? false;
  const polyOk = statusData?.polymarket ?? false;
  const isAdmin = user?.role === "admin";

  const handleRestart = async () => {
    if (restarting) return;
    if (!window.confirm(
      "Restart the Sentinel Brain?\n\n" +
      "• Open positions will NOT be closed\n" +
      "• Trading engine will pause for ~5 seconds"
    )) return;
    setRestarting(true);
    try { await API.post("/restart"); } catch {}
    const poll = setInterval(async () => {
      try {
        const res = await API.get("/health");
        if (res.data?.status === "ok") { clearInterval(poll); setRestarting(false); window.location.reload(); }
      } catch {}
    }, 2000);
    setTimeout(() => { clearInterval(poll); setRestarting(false); }, 30000);
  };

  const tabs = [
    { key: "live",    label: "📡 Live" },
    { key: "history", label: "📋 History" },
    { key: "returns", label: "💹 Returns" },
  ];
  if (isAdmin) {
    tabs.push({ key: "mt5",      label: "🖥️ MT5" });
    tabs.push({ key: "users",    label: "👥 Users" });
    tabs.push({ key: "system",   label: "⚙️ System" });
    tabs.push({ key: "settings", label: "🔧 Settings" });
  }

  const dots = [
    { label: "Brain", ok: brainOnline },
    { label: "MT5",   ok: mt5Ok },
    { label: "API",   ok: apiOk },
    { label: "WS",    ok: wsConnected },
    { label: "yFin",  ok: yfOk },
    { label: "Poly",  ok: polyOk },
  ];

  return (
    <div className="sentinel-header">
      {/* Brand */}
      <div className="sentinel-header-brand">
        {/* The version comes from /status. The fallback is deliberately NOT a
            hardcoded number: this said "5.4" while the brain ran v5.15, so a
            stale literal here shows a WRONG version rather than an unknown one. */}
        <div className="title">🛡️ SENTINEL {statusData?.version ? `v${statusData.version}` : "…"}</div>
        <div className="sub">Benjamin Muderhwa — Autonomous Trading</div>
      </div>

      {/* Tabs */}
      <div className="sentinel-tabs">
        {tabs.map((t) => (
          <button key={t.key} className="sentinel-tab" style={s.tab(tab === t.key)}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Status */}
      <div className="sentinel-status">
        {dots.map(({ label, ok }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
            <div style={s.dot(ok)} />
            <span className="status-label" style={s.label(ok)}>{label}</span>
          </div>
        ))}

        <span style={s.userBadge(user?.role)} title={`${user?.username} (${user?.role})`}>
          {user?.username?.slice(0, 8)}
        </span>

        <button style={s.logoutBtn} onClick={onLogout} title="Logout">🚪</button>
      </div>
    </div>
  );
}
